import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, MapPin, Heart, Compass, Users, PawPrint } from 'lucide-react';
import { cn } from '@/lib/utils';

const Explore = () => {
  const { t } = useTranslation();

  type HubCard = {
    id: string;
    title: string;
    subtitle: string;
    to: string;
    icon: typeof Briefcase;
    gradient: string;
    iconBg: string;
    wide?: boolean;
  };

  const hubCards: HubCard[] = [
    {
      id: 'services',
      title: t('explore.services', 'Pet Services Directory'),
      subtitle: t('explore.servicesSub', 'Vets, groomers, walkers & boarding'),
      to: '/services',
      icon: Briefcase,
      gradient: 'from-indigo-500 via-purple-500 to-pink-500',
      iconBg: 'bg-white/25',
    },
    {
      id: 'places',
      title: t('explore.places', 'Pet Friendly Places'),
      subtitle: t('explore.placesSub', 'Parks, cafés, hotels & shops'),
      to: '/places',
      icon: MapPin,
      gradient: 'from-emerald-400 via-teal-500 to-cyan-600',
      iconBg: 'bg-white/25',
    },
    {
      id: 'categories',
      title: t('categories.petCategories', 'Pet Categories'),
      subtitle: t('categories.subtitle', 'Browse pets by species'),
      to: '/pet-categories',
      icon: PawPrint,
      gradient: 'from-violet-500 via-fuchsia-500 to-pink-500',
      iconBg: 'bg-white/25',
      wide: true,
    },
    {
      id: 'matches',
      title: t('explore.matches', 'Discover Matches'),
      subtitle: t('explore.matchesSub', 'Playdates & breeding compatibility'),
      to: '/matches/discover',
      icon: Heart,
      gradient: 'from-rose-400 via-pink-500 to-fuchsia-600',
      iconBg: 'bg-white/25',
    },
    {
      id: 'community',
      title: t('explore.community', 'Community'),
      subtitle: t('explore.communitySub', 'Featured memories, lost & adoption'),
      to: '/community',
      icon: Users,
      gradient: 'from-amber-400 via-orange-500 to-red-500',
      iconBg: 'bg-white/25',
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 pt-4 pb-24 space-y-6">
      <header className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-pet-purple to-pet-blue text-white shadow-md">
          <Compass className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-xl font-bold leading-tight">
            {t('explore.title', 'Explore')}
          </h1>
          <p className="text-xs text-muted-foreground">
            {t('explore.subtitle', 'Discover pets, services, places and friends')}
          </p>
        </div>
      </header>

      {/* iOS-style bento hub with wide Pet Categories row */}
      <section className="grid grid-cols-2 gap-3">
        {hubCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.id}
              to={card.to}
              className={cn(
                'group relative overflow-hidden rounded-3xl p-4 flex flex-col justify-between text-white shadow-md hover:shadow-xl active:scale-[0.98] transition-all bg-gradient-to-br',
                card.gradient,
                card.wide ? 'col-span-2 aspect-[2.4/1]' : 'aspect-[1/1.05]'
              )}
            >
              <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent" />
              <span aria-hidden className="pointer-events-none absolute -right-6 -bottom-6 h-24 w-24 rounded-full bg-white/10 blur-2xl" />

              <Icon
                aria-hidden
                className={cn(
                  'pointer-events-none absolute text-white/15',
                  card.wide
                    ? '-right-4 -bottom-6 h-32 w-32'
                    : '-right-3 -bottom-3 h-24 w-24'
                )}
                strokeWidth={1.5}
              />

              <span className={cn('relative flex h-12 w-12 items-center justify-center rounded-2xl backdrop-blur-md shadow-inner shadow-white/10', card.iconBg)}>
                <Icon className="h-6 w-6" strokeWidth={2.2} />
              </span>

              <div className="relative">
                <div className="font-bold text-base leading-tight drop-shadow-sm">{card.title}</div>
                <div className="text-[11px] text-white/85 leading-snug mt-1 line-clamp-2">{card.subtitle}</div>
              </div>
            </Link>
          );
        })}
      </section>
    </div>
  );
};

export default Explore;
